import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContactForm from './components/ContactForm';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 — 亚洲免费在线',
    description: '了解亚洲免费在线平台 — 您的亚洲优质免费网络资源首选平台',
    url: 'https://akeliusi.cn/about',
    about: {
      '@type': 'Organization',
      name: '亚洲免费在线',
      description: '亚洲免费在线资源汇总平台，提供优质的亚洲免费在线内容、工具与服务',
      foundingDate: '2024',
      url: 'https://akeliusi.cn',
    },
  };

  const testimonials = [
    {
      name: '张伟',
      role: '前端开发者',
      content: '亚洲免费在线是我发现最有价值的资源汇总网站之一。通过这个平台，我找到了很多好用的免费开发工具和设计素材，大大提升了工作效率。推荐给所有需要在线资源的朋友！',
      avatarBg: 'bg-accent-100 text-accent-700',
    },
    {
      name: '李美玲',
      role: '自由设计师',
      content: '作为设计师，免费的素材和工具对我来说非常重要。亚洲免费在线的分类清晰，推荐质量高，每次浏览都能发现新的好东西。已经给团队里每个人都推荐了！',
      avatarBg: 'bg-primary-100 text-primary-700',
    },
    {
      name: '王浩然',
      role: '大学生',
      content: '作为一名学生，预算有限，免费的学习资源对我来说特别宝贵。这个平台帮我找到了很多优质的免费课程和学习工具，让我的自学之路更加顺畅。',
      avatarBg: 'bg-secondary-100 text-secondary-700',
    },
    {
      name: '陈思雨',
      role: '内容创作者',
      content: '做自媒体的过程中，经常需要各种工具和素材。亚洲免费在线就像是我的资源宝库，从图片素材到音乐音效，从效率工具到AI应用，想要的几乎都能找到。',
      avatarBg: 'bg-accent-100 text-accent-700',
    },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-xs font-medium text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full">
                  关于我们
                </span>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mt-4 mb-5">
                  亚洲免费在线 — 让优质资源触手可及
                </h1>
                <div className="space-y-4 text-foreground-700 text-sm leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">亚洲免费在线</strong>成立于2024年，是专注于亚洲地区免费网络资源的综合性汇总平台。我们的使命是帮助用户在海量互联网信息中，快速发现真正优质、实用的免费在线工具、内容和服务。
                  </p>
                  <p>
                    平台覆盖<strong className="text-foreground-900">在线学习、设计工具、开发资源、影视娱乐、办公效率、图片素材、音频音乐、AI工具</strong>等八大核心分类，每一篇推荐文章都经过编辑团队的实测验证，确保推荐的资源真实有效、安全可靠。
                  </p>
                  <p>
                    我们相信，优质的数字资源应该对所有人开放。无论你是学生、设计师、开发者还是创业者，都能在这里找到用得上的免费工具和资源。让技术普惠，让创意流动，这是亚洲免费在线的初心和坚持。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20workspace%20with%20diverse%20Asian%20team%20working%20together%2C%20warm%20lighting%2C%20minimalist%20office%20design%2C%20creative%20atmosphere%2C%20natural%20light%2C%20clean%20aesthetic%2C%20professional%20yet%20relaxed%20vibe%2C%20editorial%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="亚洲免费在线团队"
                  className="w-full h-full object-cover object-top"
                  title="亚洲免费在线团队 - 亚洲免费在线"
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="bg-background-100/50 py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { num: '200+', label: '精选资源文章' },
                  { num: '8', label: '核心分类' },
                  { num: '50,000+', label: '月度活跃用户' },
                  { num: '98%', label: '用户满意度' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{stat.num}</div>
                    <div className="text-sm text-foreground-600 mt-2">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                用户口碑
              </h2>
              <p className="text-foreground-600 mt-2 text-sm">听听用户们怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${t.avatarBg}`}>
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex gap-1 mt-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="bg-background-100/50 py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  联系我们
                </h2>
                <p className="text-foreground-600 mt-2 text-sm">
                  有任何问题、建议或资源推荐，欢迎随时联系我们
                </p>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <ContactForm />
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">电子邮箱</div>
                    <a href="mailto:contact@akeliusi.cn" className="text-sm text-foreground-900 hover:text-primary-600 transition-colors">
                      contact@akeliusi.cn
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">微信公众号</div>
                    <span className="text-sm text-foreground-900">亚洲免费在线</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100/70 text-secondary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">工作时间</div>
                    <span className="text-sm text-foreground-900">周一至周五 9:00-18:00</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-100/70 text-primary-700 text-sm">
              <i className="ri-heart-line"></i>
              <span>感谢每一位用户的支持与信任，我们将持续为您发现更多优质免费资源</span>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}